import { invoke } from '@tauri-apps/api/core';

import { GENIE_COMMAND_PREFIX as COMMAND_PREFIX, formatError } from './constants';

/**
 * Request payload for uploading a consult document
 */
export interface UploadConsultRequest {
  documentType: 'Consult';
  patientId: number;
  practitionerId: number;
  text: string;
  signed?: boolean;
  dateTime?: string;
}

/**
 * Request payload for uploading a letter document
 */
export interface UploadLetterRequest {
  documentType: 'Letter';
  patientId: number;
  practitionerId: number;
  text: string;
  template?: string;
  signed?: boolean;
  recipientId?: number;
  dateTime?: string;
  ccRecipients?: number[];
}

/**
 * Array of template names returned by genieGetTemplates
 */
export type TemplateList = string[];

/**
 * Union type for all document upload requests
 */
export type UploadDocumentRequest = UploadConsultRequest | UploadLetterRequest;

/**
 * Uploads a document (Consult or Letter) to Genie for a specific patient.
 *
 * @param request - The document upload request
 * @throws Error if not connected or if the upload fails
 */
export async function genieUploadDocument(request: UploadDocumentRequest): Promise<void> {
  try {
    await invoke<void>(`${COMMAND_PREFIX}upload_document`, {
      request,
    });
  } catch (error) {
    throw new Error(`Failed to upload document: ${formatError(error)}`);
  }
}

/**
 * Gets the list of available letter templates from Genie.
 *
 * @returns Array of template names
 * @throws Error if not connected or if the request fails
 */
export async function genieGetTemplates(): Promise<TemplateList> {
  try {
    const templates = await invoke<TemplateList>(`${COMMAND_PREFIX}get_templates`);
    return templates;
  } catch (error) {
    throw new Error(`Failed to get templates: ${formatError(error)}`);
  }
}
